import * as React from 'react'
import { FileIcon } from 'lucide-react'
import { cn } from '../../lib/utils'

export interface FileDropzoneProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
	hint?: string
	dropLabel?: string
	selectLabel?: string
	accept?: string
	disabled?: boolean
	multiple?: boolean
	onFilesSelected?: (files: FileList) => void
}

function FileDropzone({
	className,
	hint,
	dropLabel,
	selectLabel,
	accept,
	disabled = false,
	multiple = false,
	onFilesSelected,
	...props
}: FileDropzoneProps) {
	const inputRef = React.useRef<HTMLInputElement>(null)
	const [isDragOver, setIsDragOver] = React.useState(false)
	const [isFocused, setIsFocused] = React.useState(false)

	const handleFiles = React.useCallback(
		(files: FileList | null) => {
			if (files && files.length > 0 && onFilesSelected) {
				onFilesSelected(files)
			}
		},
		[onFilesSelected]
	)

	const handleDragOver = React.useCallback(
		(e: React.DragEvent) => {
			e.preventDefault()
			if (!disabled) setIsDragOver(true)
		},
		[disabled]
	)

	const handleDragLeave = React.useCallback(() => {
		setIsDragOver(false)
	}, [])

	const handleDrop = React.useCallback(
		(e: React.DragEvent) => {
			e.preventDefault()
			setIsDragOver(false)
			if (!disabled) handleFiles(e.dataTransfer.files)
		},
		[disabled, handleFiles]
	)

	const handleClick = React.useCallback(() => {
		if (!disabled) inputRef.current?.click()
	}, [disabled])

	const handleKeyDown = React.useCallback(
		(e: React.KeyboardEvent) => {
			if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
				e.preventDefault()
				inputRef.current?.click()
			}
		},
		[disabled]
	)

	const handleChange = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			handleFiles(e.target.files)
			e.target.value = ''
		},
		[handleFiles]
	)

	return (
		<div
			data-slot="file-dropzone"
			role="button"
			tabIndex={disabled ? -1 : 0}
			aria-disabled={disabled}
			onClick={handleClick}
			onKeyDown={handleKeyDown}
			onDragOver={handleDragOver}
			onDragLeave={handleDragLeave}
			onDrop={handleDrop}
			onFocus={() => setIsFocused(true)}
			onBlur={() => setIsFocused(false)}
			className={cn(
				'flex flex-col items-center gap-3 overflow-hidden rounded-md border border-dashed border-(--color-border) bg-(--color-popover) px-3 py-6 transition-shadow',
				'cursor-pointer',
				(isFocused || isDragOver) &&
					'shadow-[0px_0px_0px_2px_var(--color-background),0px_0px_0px_4px_var(--color-primary)]',
				disabled && 'pointer-events-none opacity-50',
				className
			)}
			{...props}
		>
			<input
				ref={inputRef}
				type="file"
				className="hidden"
				accept={accept}
				multiple={multiple}
				onChange={handleChange}
				tabIndex={-1}
			/>

			<div className="flex size-10 items-center justify-center rounded-lg bg-(--color-secondary)">
				<FileIcon className="size-4 text-(--color-muted-foreground)" />
			</div>

			<div className="flex flex-col items-center">
				{(dropLabel || selectLabel) && (
					<p className="flex items-center gap-1 text-sm leading-6 text-(--color-foreground)">
						{dropLabel}
						{selectLabel && (
							<span className="font-medium text-(--color-brand)">{selectLabel}</span>
						)}
					</p>
				)}
				{hint && (
					<p className="text-center text-xs leading-4 text-(--color-muted-foreground)">
						{hint}
					</p>
				)}
			</div>
		</div>
	)
}

FileDropzone.displayName = 'FileDropzone'
export { FileDropzone }
